/**
 * product-stats.js — counts each stat figure up from zero the first time it
 * scrolls into view.
 *
 * Only the digits animate; any surrounding characters ("96%", "1.5x", "+40")
 * are preserved by splitting the label into a prefix, a number and a suffix.
 * A figure with no digits is left alone. Runs once per element.
 *
 * Standalone asset, decoupled from theme.dev.js.
 */
(function () {
  'use strict';

  const DURATION = 1400;
  const NUMBER = /^(\D*?)([\d.,]+)(.*)$/s;

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function countUp(el) {
    if (el.dataset.counted) return;
    el.dataset.counted = 'true';

    const parts = el.textContent.trim().match(NUMBER);
    if (!parts) return;

    const [, prefix, rawNumber, suffix] = parts;
    const decimals = (rawNumber.split('.')[1] || '').length;
    const target = parseFloat(rawNumber.replace(/,/g, ''));
    if (!isFinite(target)) return;

    if (reduceMotion) return;

    const grouped = rawNumber.includes(',');
    const render = (value) => {
      let out = value.toFixed(decimals);
      if (grouped) {
        const [int, dec] = out.split('.');
        out = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (dec ? '.' + dec : '');
      }
      el.textContent = prefix + out + suffix;
    };

    const settle = () => {
      el.textContent = prefix + rawNumber + suffix;
      el.dataset.counted = 'done';
      document.removeEventListener('visibilitychange', onHide);
    };

    // requestAnimationFrame stops firing while the tab is hidden, which would
    // otherwise strand the figure part-way. Both guards land on the real number.
    const onHide = () => {
      if (document.hidden) settle();
    };
    document.addEventListener('visibilitychange', onHide);
    const safety = setTimeout(settle, DURATION + 400);

    render(0);

    const start = performance.now();
    const step = (now) => {
      if (el.dataset.counted === 'done') return;
      const progress = Math.min(1, (now - start) / DURATION);
      render(target * easeOut(progress));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        clearTimeout(safety);
        settle();
      }
    };
    requestAnimationFrame(step);
  }

  function init() {
    const figures = document.querySelectorAll(
      '.product-stats__hero-value, .product-stats__value'
    );
    if (!figures.length) return;

    if (!('IntersectionObserver' in window)) {
      figures.forEach((el) => (el.dataset.counted = 'true'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    figures.forEach((el) => observer.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
